import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * GoldenParticles — performance-safe ambient particle background.
 * Renders small absolute-positioned divs that gently drift upward and pulse.
 * Respects prefers-reduced-motion (renders nothing).
 *
 * Use as a sibling layer with `pointer-events-none` over a section.
 */
function makeParticles(n) {
  return Array.from({ length: n }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: 60 + Math.random() * 60,
    size: 1 + Math.random() * 2.4,
    duration: 9 + Math.random() * 9,
    delay: Math.random() * 6,
    opacity: 0.25 + Math.random() * 0.45,
    yTarget: -260 - Math.random() * 140,
  }));
}

export default function GoldenParticles({
  count = 14,
  className = "",
  density = "normal", // 'subtle' | 'normal' | 'dense'
}) {
  const reduced = useReducedMotion();
  const n =
    density === "subtle"
      ? Math.max(8, count - 4)
      : density === "dense"
        ? count + 6
        : count;
  // useMemo with deterministic input so the random set is stable per mount
  const particles = useMemo(() => (reduced ? [] : makeParticles(n)), [n, reduced]);

  if (reduced || particles.length === 0) return null;

  return (
    <div
      data-testid="golden-particles"
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {particles.map((p) => (
        <motion.span
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            backgroundColor: "#F9DF9F",
            boxShadow: "0 0 6px rgba(249, 223, 159, 0.6)",
            willChange: "transform, opacity",
          }}
          initial={{ y: 0, opacity: 0 }}
          animate={{
            y: p.yTarget,
            opacity: [0, p.opacity, p.opacity, 0],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: "easeOut",
            repeat: Infinity,
            repeatDelay: 0.5,
          }}
        />
      ))}
    </div>
  );
}
